import React from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import GetAppIcon from '@material-ui/icons/GetApp';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import { messagesRequest } from 'modules/messages/actions';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import {
  BulkClientsFields,
  EnvironmentFields,
  ListBulkClientsProps,
} from 'modules/environments/interfaces';
import i18n from 'i18n';
import palette from 'theme/palette';
import Button from 'components/Button';
import { modalClose } from 'modules/modal/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import { getBulkClientsRequest } from 'modules/environments/actions';
import { SearchBar } from 'components/SearchBar';
import styles from '../styles';
import { initialQuery } from '..';

const useStyles = makeStyles(styles);

export interface EmailAdminProps {
  bulkClients: ListBulkClientsProps;
  isLoadingBulkClients: boolean;
  environment?: EnvironmentFields | null;
  modalClose: () => void;
  getBulkClientsRequest: (props) => void;
}

const EmailAdmin = ({
  bulkClients,
  isLoadingBulkClients,
  environment,
  modalClose,
  getBulkClientsRequest,
}: EmailAdminProps) => {
  const classes = useStyles();

  const {
    query,
    searchValue,
    itemOrder,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const getClientsListPDF: Array<[
    string,
    string
  ]> = bulkClients?.results.map(item => [
    item?.name,
    item?.email_admin.email_admin,
  ]);

  const headers = [
    {
      key: 'nome',
      i18n: 'CLIENT_NAME_KEY',
      order: true,
    },
    {
      key: 'email_admin',
      i18n: 'Email',
      order: true,
    },
  ];

  const getBulkClients = () => {
    getBulkClientsRequest({
      id: environment?.environment_uuid,
      query: { ...query },
    });
  };

  React.useEffect(() => {
    getBulkClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBulkClientsRequest, query]);

  // eslint-disable-next-line new-cap
  const doc = new jsPDF();

  const generatePDFButton = () => {
    doc.setFontSize(16);
    doc.text(
      [
        '',
        `${i18n.t('ENVIRONMENT_NAME_KEY')}: ${environment?.name}`,
        `${i18n.t('PDF_GENERATED_AT_KEY')}: ${new Date().toLocaleString(
          'pt-BR',
          {
            hour12: false,
          }
        )}`,
        `${i18n.t('CLIENTS_PAGE.LIST_CLIENTS_TITLE_KEY')}:`,
        '',
      ],
      10,
      10,
      { lineHeightFactor: 1.5, maxWidth: 200 }
    );
    autoTable(doc, {
      head: [[`${i18n.t('CLIENT_NAME_KEY')}`, 'Email']],
      margin: { top: 50 },
      styles: { lineWidth: 0.3, lineColor: palette.borderGray, fontSize: 11 },
      headStyles: {
        fillColor: palette.deepBlue,
        textColor: palette.offWhite,
        fontSize: 11,
      },
      body: getClientsListPDF,
    });
    doc.save(`${environment?.name}-clients-email-admin-details.pdf`);
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid container>
        <SearchBar
          id="search-client"
          isLoading={isLoadingBulkClients}
          searchValue={searchValue}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  order={item?.order}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  markOderItem={itemOrder === item?.key}
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: BulkClientsFields, index) => (
            <TableRow key={index}>
              <TableCell>{item?.name}</TableCell>
              <TableCell>
                {item?.email_admin.email_admin.replace(/,/g, ', ')}
              </TableCell>
            </TableRow>
          )}
          overrideRowsCount={10}
          colCount={2}
          isLoading={isLoadingBulkClients}
          data={bulkClients}
          onChangeLimit={handleLimitChange}
          onChangePage={handlePageChange}
          notFoundText={i18n.t('NO_CLIENT_KEY')}
        />
      </Grid>
      <Grid container justify="flex-end" className={classes.buttons}>
        <Button
          id="generate-pdf-button"
          submitButton
          startIcon={<GetAppIcon />}
          onClick={() => {
            generatePDFButton();
            modalClose();
            messagesRequest({
              type: 'success',
              message: i18n.t('PDF_GENERATED_SUCCESSFULLY_KEY'),
            });
          }}
        >
          {i18n.t('GENERATE_PDF')}
        </Button>
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CLOSE_KEY')}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { bulkClients, isLoadingBulkClients },
}: ApplicationState) => ({
  role: data?.role,
  bulkClients,
  isLoadingBulkClients,
});

export default connect(mapStateToProps, { modalClose, getBulkClientsRequest })(
  EmailAdmin
);
